'use client';

import * as React from 'react';

type WhatsAppButtonProps = {
  /** WhatsApp click-to-chat link (e.g. https://wa.me/message/XXXX or https://wa.me/<phone>). */
  chatUrl?: string;
  /** Header title text (desktop popup). */
  headerTitle?: string;
  /** Header subtitle text (desktop popup). */
  headerSubtitle?: string;
  /** Greeting chat bubble shown above the QR (desktop popup). */
  greetingMessage?: string;
  /** Caption shown below the QR code (desktop popup). */
  qrInstruction?: string;
  /** Path to the QR code image (placed in /public). */
  qrImageSrc?: string;
  /** Label for the primary call-to-action button. */
  ctaLabel?: string;
  /** Which corner the widget floats in. */
  position?: 'left' | 'right';
};

const DEFAULT_CHAT_URL = 'https://wa.me/919025525350';

const COLORS = {
  brand: '#25D366', // WhatsApp green (launcher + CTA)
  header: '#2f7d6e', // muted teal header
  chatBg: '#e5ddd5' // classic WhatsApp chat backdrop
};

// WhatsApp doodle backdrop for the chat area.
const CHAT_TEXTURE: React.CSSProperties = {
  backgroundColor: COLORS.chatBg,
  backgroundImage: 'url(/assets/Whatsapp_BG.png)',
  backgroundSize: '380px auto',
  backgroundRepeat: 'repeat'
};

function WhatsAppLogo({
  className,
  style
}: {
  className?: string;
  style?: React.CSSProperties;
}): React.JSX.Element {
  return (
    <svg
      viewBox="0 0 448 512"
      className={className}
      style={style}
      fill="currentColor"
      aria-hidden="true"
      focusable="false"
    >
      <path d="M380.9 97.1C339 55.1 283.2 32 223.9 32c-122.4 0-222 99.6-222 222 0 39.1 10.2 77.3 29.6 111L0 480l117.7-30.9c32.4 17.7 68.9 27 106.1 27h.1c122.3 0 224.1-99.6 224.1-222 0-59.3-25.2-115-67.1-157zm-157 341.6c-33.2 0-65.7-8.9-94-25.7l-6.7-4-69.8 18.3L72 359.2l-4.4-7c-18.5-29.4-28.2-63.3-28.2-98.2 0-101.7 82.8-184.5 184.6-184.5 49.3 0 95.6 19.2 130.4 54.1 34.8 34.9 56.2 81.2 56.1 130.5 0 101.8-84.9 184.6-186.6 184.6zm101.2-138.2c-5.5-2.8-32.8-16.2-37.9-18-5.1-1.9-8.8-2.8-12.5 2.8-3.7 5.6-14.3 18-17.6 21.8-3.2 3.7-6.5 4.2-12 1.4-32.6-16.3-54-29.1-75.5-66-5.7-9.8 5.7-9.1 16.3-30.3 1.8-3.7.9-6.9-.5-9.7-1.4-2.8-12.5-30.1-17.1-41.2-4.5-10.8-9.1-9.3-12.5-9.5-3.2-.2-6.9-.2-10.6-.2-3.7 0-9.7 1.4-14.8 6.9-5.1 5.6-19.4 19-19.4 46.3 0 27.3 19.9 53.7 22.6 57.4 2.8 3.7 39.1 59.7 94.8 83.8 35.2 15.2 49 16.5 66.6 13.9 10.7-1.6 32.8-13.4 37.4-26.4 4.6-13 4.6-24.1 3.2-26.4-1.3-2.5-5-3.9-10.5-6.6z" />
    </svg>
  );
}

export function WhatsAppButton({
  chatUrl = DEFAULT_CHAT_URL,
  headerTitle = 'VideoMule',
  headerSubtitle = 'Typically replies within an hour',
  greetingMessage = 'Hello, is there anything we can assist you with?',
  qrInstruction = 'Scan with your phone camera',
  qrImageSrc = '/assets/whatsapp-qr.png',
  ctaLabel = 'Chat with us',
  position = 'right'
}: WhatsAppButtonProps): React.JSX.Element {
  const [isOpen, setIsOpen] = React.useState(false);
  // Detect touch phones/tablets so the launcher can deep-link straight into the
  // WhatsApp app instead of showing the desktop QR popup. Starts false to keep
  // SSR/first paint stable, then resolves on mount.
  const [isMobile, setIsMobile] = React.useState(false);

  const isLeft = position === 'left';
  // The Next.js dev indicator sits at bottom-left only in development. When the
  // widget shares that corner, lift it so the two never overlap; in production
  // the badge is gone, so it sits flush in the corner.
  const avoidDevBadge = isLeft && process.env.NODE_ENV === 'development';

  React.useEffect(() => {
    const mediaQuery = window.matchMedia('(hover: none) and (pointer: coarse)');
    const update = () => setIsMobile(mediaQuery.matches);
    update();
    mediaQuery.addEventListener('change', update);
    return () => mediaQuery.removeEventListener('change', update);
  }, []);

  // Close on Escape, regardless of focus location.
  React.useEffect(() => {
    if (!isOpen) {
      return;
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [isOpen]);

  const openWhatsApp = () => {
    window.open(chatUrl, '_blank', 'noopener,noreferrer');
  };

  const handleLauncherClick = () => {
    // On mobile, skip the QR popup and open the WhatsApp app directly.
    if (isMobile) {
      openWhatsApp();
      return;
    }
    setIsOpen((open) => !open);
  };

  return (
    <div
      className={`pointer-events-none fixed z-[9999] flex flex-col ${
        isLeft ? 'left-5 items-start' : 'right-5 items-end'
      } ${avoidDevBadge ? 'bottom-24' : 'bottom-5'}`}
    >
      {/* Desktop QR popup */}
      <div
        role="dialog"
        aria-label="WhatsApp chat"
        aria-hidden={!isOpen}
        className={`mb-3 w-[calc(100vw-2.5rem)] max-w-[22rem] origin-bottom overflow-hidden rounded-3xl bg-white shadow-2xl ring-1 ring-black/5 transition-all duration-300 ease-out ${
          isLeft ? 'origin-bottom-left' : 'origin-bottom-right'
        } ${
          isOpen
            ? 'pointer-events-auto translate-y-0 scale-100 opacity-100'
            : 'pointer-events-none translate-y-2 scale-95 opacity-0'
        }`}
      >
        {/* Header */}
        <div
          className="flex items-center gap-3 px-5 py-4 text-white"
          style={{ backgroundColor: COLORS.header }}
        >
          <span className="relative shrink-0">
            <span
              className="flex size-11 items-center justify-center rounded-full ring-4 ring-white/15"
              style={{ backgroundColor: COLORS.brand }}
            >
              <WhatsAppLogo className="size-7 text-white" />
            </span>
            {/* Online status dot */}
            <span
              className="absolute bottom-0 right-0 size-3 rounded-full border-2 border-white"
              style={{ backgroundColor: COLORS.brand }}
              aria-hidden="true"
            />
          </span>
          <div className="min-w-0 flex-1">
            <p className="truncate text-base font-bold leading-tight">
              {headerTitle}
            </p>
            <p className="truncate text-xs leading-tight text-white/85">
              {headerSubtitle}
            </p>
          </div>
        </div>

        {/* Chat area — beige doodle backdrop */}
        <div
          className="flex flex-col gap-3 px-4 py-5"
          style={CHAT_TEXTURE}
        >
          {/* Incoming greeting bubble */}
          <div className="max-w-[85%] self-start rounded-2xl rounded-tl-sm bg-white px-4 py-3 shadow-sm">
            <p className="text-sm leading-relaxed text-gray-800">
              {greetingMessage}
            </p>
          </div>

          {/* Typing indicator bubble */}
          <div className="flex items-center gap-1.5 self-end rounded-2xl rounded-tr-sm bg-white px-4 py-3 shadow-sm">
            <span className="size-1.5 animate-bounce rounded-full bg-gray-400 [animation-delay:-0.3s]" />
            <span className="size-1.5 animate-bounce rounded-full bg-gray-400 [animation-delay:-0.15s]" />
            <span className="size-1.5 animate-bounce rounded-full bg-gray-400" />
          </div>

          {/* QR card */}
          <div className="mt-1 rounded-2xl bg-white p-5 shadow-sm">
            <div className="relative mx-auto w-fit">
              <img
                src={qrImageSrc}
                alt="Scan to chat with us on WhatsApp"
                width={160}
                height={160}
                className="size-40 object-contain"
              />
              {/* WhatsApp logo overlaid at the QR center */}
              <span className="absolute left-1/2 top-1/2 flex size-9 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-lg bg-white shadow">
                {/* green logo on white keeps it readable at the QR center */}
                <WhatsAppLogo
                  className="size-6"
                  style={{ color: COLORS.brand }}
                />
              </span>
            </div>
            <p className="mt-4 text-center text-sm text-gray-500">
              {qrInstruction}
            </p>
          </div>
        </div>

        {/* CTA footer */}
        <div className="bg-white px-5 py-4">
          <button
            type="button"
            onClick={openWhatsApp}
            className="flex w-full items-center justify-center gap-2 rounded-full px-4 py-2.5 text-sm font-bold text-white shadow-sm transition-transform hover:scale-[1.02] focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 active:scale-95"
            style={
              {
                backgroundColor: COLORS.brand,
                '--tw-ring-color': COLORS.brand
              } as React.CSSProperties
            }
          >
            <WhatsAppLogo className="size-5" />
            {ctaLabel}
          </button>
        </div>
      </div>

      {/* Floating launcher button */}
      <div className="pointer-events-auto relative">
        {/* Soft attention pulse — only while closed; hidden for reduced motion */}
        {!isOpen && (
          <span
            aria-hidden="true"
            className="absolute inset-0 rounded-full [animation-duration:2.5s] motion-reduce:hidden animate-ping"
            style={{ backgroundColor: COLORS.brand, opacity: 0.35 }}
          />
        )}
        <button
          type="button"
          onClick={handleLauncherClick}
          aria-label={isOpen ? 'Close WhatsApp chat' : 'Open WhatsApp chat'}
          aria-expanded={isOpen}
          className="relative flex size-[60px] cursor-pointer items-center justify-center rounded-full text-white shadow-lg transition-all duration-200 hover:scale-110 focus:outline-none focus-visible:ring-4 focus-visible:ring-offset-2 active:scale-95"
          style={
            {
              backgroundColor: COLORS.brand,
              '--tw-ring-color': COLORS.brand
            } as React.CSSProperties
          }
        >
          {isOpen ? (
            <svg
              viewBox="0 0 24 24"
              className="size-8"
              fill="none"
              stroke="currentColor"
              strokeWidth={2}
              strokeLinecap="round"
              aria-hidden="true"
            >
              <circle cx="12" cy="12" r="9" />
              <path d="M9 9l6 6M15 9l-6 6" />
            </svg>
          ) : (
            <WhatsAppLogo className="size-8" />
          )}
        </button>
      </div>
    </div>
  );
}
